import { MemoizedLayoutBlockRenderer } from '@unifyapps/carbon/no-code/components/MemoizedLayoutBlockRenderer';
import {
  applyEffectsStackingContext,
  useStackEffects,
} from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles/effects/useStackEffects';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { getBlockKey } from '@unifyapps/carbon/no-code/utils/block';
import { UA_INTERFACE_ENABLE_HOVER_EFFECT } from '@unifyapps/style/themes/consts';
import { default as StackPrimitive } from '@unifyapps/ui/_components/Stack';
import { clsx } from 'clsx';
import { type CSSProperties, useMemo } from 'react';
import { useBlockInteractions } from '../hooks/useBlockInteractions';
import type { StackProps } from './BaseStack';
import { useStackBaseStyles } from './useStackBaseStyles';
import { getDirection } from './utils';

// Mounted only when `appearance.effects` has at least one entry. All effect-
// specific hooks, memos, and overlay JSX live here — the Stack gate in
// Stack.tsx keeps them out of the no-effects path entirely.
export function EffectsStack({
  blockState,
  attributesAndEventListeners,
  computedBlockState,
  events,
  registry,
  dndRef,
  block,
}: StackProps) {
  const { content, appearance } = computedBlockState;

  const { className, wrapClassName, finalStyle, blurStyle, linearGradientStyle, BlockRenderer } =
    useStackBaseStyles({ appearance, block, registry });

  const {
    effectsStyle,
    hasBackgroundBlur,
    hasInnerShadowOverlay,
    backgroundBlurOverlayStyle,
    innerShadowOverlayStyle,
  } = useStackEffects(appearance?.effects);

  // effects spread last so a configured shadow wins over legacy
  // `styles.boxShadow`; same for blur over blurConfig.
  const mergedStyle = useMemo(() => {
    const merged = {
      ...finalStyle,
      ...blurStyle,
      ...linearGradientStyle,
      ...effectsStyle,
    } as CSSProperties;
    return applyEffectsStackingContext(merged, hasBackgroundBlur, hasInnerShadowOverlay);
  }, [
    finalStyle,
    blurStyle,
    linearGradientStyle,
    effectsStyle,
    hasBackgroundBlur,
    hasInnerShadowOverlay,
  ]);

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);
  const { onClick, onMouseEnter, longPressProps } = useBlockInteractions({
    events,
    emitEvent,
    blockType: 'Stack',
  });

  return (
    <StackPrimitive
      alignItems={appearance?.alignItems}
      className={clsx(className, wrapClassName, {
        [`${UA_INTERFACE_ENABLE_HOVER_EFFECT} cursor-pointer`]: Boolean(onClick),
      })}
      direction={getDirection(appearance)}
      justifyContent={appearance?.justifyContent}
      ref={dndRef}
      style={mergedStyle}
      {...attributesAndEventListeners}
      {...longPressProps}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      {hasBackgroundBlur ? <div aria-hidden="true" style={backgroundBlurOverlayStyle} /> : null}
      {content.blockIds?.map((blockId, index) => (
        <MemoizedLayoutBlockRenderer
          BlockRenderer={BlockRenderer}
          blockId={blockId}
          index={index}
          // eslint-disable-next-line react/no-array-index-key
          key={getBlockKey(blockId, index)}
          parentBlockId={blockState.id}
        />
      ))}
      {innerShadowOverlayStyle ? <div aria-hidden="true" style={innerShadowOverlayStyle} /> : null}
    </StackPrimitive>
  );
}
